import React from 'react';
import {
  Accordion, AccordionDetails, AccordionSummary, Avatar, IconButton, TablePagination, Typography,
} from '@mui/material';
import PermMediaIcon from '@mui/icons-material/PermMedia';
import { selectAuthUser } from 'base/auth/selectors';
import { selectSingleConversation } from 'pages/sessions/components/message-hub/selectors';
import { useSelector } from 'react-redux';
import { IAttachmentDto, IMessage } from 'pages/sessions/components/message-hub/interface';
import { IMediaItemData } from 'base/common/media-modal/interface';
import { dateTimeFormat } from 'utils/formats';
import { getUserName } from 'utils/common-utils';
import { ArrowSquareDownIcon } from '../../../../../../assets/icons';
import { PrimaryLightBgCard } from '../../../../../../components';
import { AccordionStyle, ThumbnailImgStyle } from '../../styles';

const UserMedia = ({ onMediaSelect }: { onMediaSelect: (media: IMediaItemData) => void; }) => {
  const { users, messages } = useSelector(selectSingleConversation);
  const { id: authExpertId } = useSelector(selectAuthUser);
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(5);

  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number,
  ) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const userMessages: IMessage[] | null = React.useMemo(
    () => messages.filter(
      (message) => message.sent_by !== authExpertId && message.attachments.length > 0,
    ),
    [authExpertId, messages],
  );

  const handleMediaSelect = (media: IAttachmentDto) => {
    onMediaSelect({
      id: Math.random().toString(),
      title: media.fileName,
      fileType: media.fileType,
      fileCategory: media.fileCategory || 'image',
      thumbnail: media.thumbnail ?? '',
      contentUrl: media.fileUrl,
    });
  };

  const userMedias = [];
  if (users && users.length > 0) {
    if (userMessages && userMessages.length > 0) {
      [...userMessages].splice(page * rowsPerPage, rowsPerPage).forEach((userMessage) => {
        userMessage.attachments.forEach((attachment) => {
          userMedias.push(
            <IconButton
              key={`${Math.random()} ${attachment.fileName}`}
              disableRipple
              sx={{ p: 0, display: 'flex', width: '100%' }}
              onClick={() => { handleMediaSelect(attachment); }}
            >
              <PrimaryLightBgCard className="flex-grow-1 d-flex flex-nowrap overflow-hidden" sx={{ p: 1.5, mb: 1 }}>
                <Avatar
                  variant="rounded"
                  alt="The image"
                  src={attachment.fileUrl}
                  sx={ThumbnailImgStyle}
                >
                  <PermMediaIcon sx={{ fontSize: 16 }} />
                </Avatar>
                <div className="text-start">
                  <Typography variant="body2" sx={{ lineHeight: 1.2, mb: 0.5 }}>
                    {attachment.fileName}
                  </Typography>
                  {userMessage.createdAt && userMessage.createdAt.length ? (
                    <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1 }}>
                      {dateTimeFormat(userMessage.createdAt)}
                    </Typography>
                  ) : null}
                </div>
              </PrimaryLightBgCard>
            </IconButton>,
          );
        });
      });
    } else {
      userMedias.push(
        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
          {`No media received from ${getUserName(users[0])}`}
        </Typography>,
      );
    }
  }

  return (
    <Accordion
      sx={[AccordionStyle, {
        '.MuiCollapse-root': {
          mb: 1,
        },
      }]}
      defaultExpanded
      disableGutters
    >
      <AccordionSummary
        expandIcon={<ArrowSquareDownIcon sx={{ color: 'text.secondary', fontSize: 16 }} />}
        aria-controls="panel1a-content"
        id="panel1a-header"
      >
        {users && users.length > 0 && (
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {`${users && users.length > 0 ? getUserName(users[0]) : ''}'s media`}
          </Typography>
        )}
      </AccordionSummary>

      <AccordionDetails>
        {userMedias}
        <TablePagination
          component="div"
          count={userMessages.length}
          page={page}
          rowsPerPageOptions={[]}
          labelRowsPerPage=""
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </AccordionDetails>
    </Accordion>
  );
};

export default UserMedia;
